import { useEffect, useMemo, useState } from 'react';
import { EngineError, getCaseAudit, getDocumentPdfUrl, listCaseDocuments, listCases } from '../../api/engineDashboard';
import { mapLimit } from '../../lib/caseInsights';
import { caseTitle, docTypeLabel } from '../../lib/labels';
import { formatDate } from '../../lib/format';
import type { CaseListItem, DocumentRecord } from '../../types/dashboard';

interface DocRow {
  doc: DocumentRecord;
  caseItem: CaseListItem;
}

interface Props {
  token: string;
  onOpenCase: (caseId: string) => void;
  onUnauthorized: () => void;
}

const CASE_SCAN_LIMIT = 40;

/**
 * Clerk documents view: read-only list of ROMs / Stipulations across the
 * court's cases. Primary source is GET /cases/:id/documents; where that
 * endpoint isn't open to the clerk's session yet, the case audit trail's
 * document.generated entries fill in (id, type, pdf link).
 */
export function ClerkDocumentsPage({ token, onOpenCase, onUnauthorized }: Props) {
  const [rows, setRows] = useState<DocRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [sort, setSort] = useState<'created_at' | 'doc_type'>('created_at');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listCases(token, { limit: 100 });
        const targets = res.data.slice(0, CASE_SCAN_LIMIT);
        const collected: DocRow[] = [];
        await mapLimit(targets, 5, async (c) => {
          let docs: DocumentRecord[] = [];
          try {
            docs = (await listCaseDocuments(token, c.id)).documents;
          } catch {
            // Fall back to audit-derived document records.
            try {
              const audit = await getCaseAudit(token, c.id);
              docs = audit.data
                .filter((e) => e.event_type === 'document.generated')
                .map((e): DocumentRecord | null => {
                  const d = (e.details ?? {}) as {
                    agreement_id?: string;
                    doc_type?: string;
                    pdf_url?: string | null;
                  };
                  return d.agreement_id
                    ? {
                        id: d.agreement_id,
                        case_id: c.id,
                        session_id: null,
                        doc_type: d.doc_type ?? 'document',
                        status: 'generated',
                        pdf_url: d.pdf_url ?? null,
                        created_at: e.timestamp,
                      }
                    : null;
                })
                .filter((d): d is DocumentRecord => d !== null);
            } catch {
              /* case unreadable — skip */
            }
          }
          for (const doc of docs) collected.push({ doc, caseItem: c });
        });
        if (!cancelled) setRows(collected);
      } catch (err) {
        if (err instanceof EngineError && err.status === 401) {
          onUnauthorized();
          return;
        }
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load documents');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const sorted = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) =>
      sort === 'doc_type'
        ? a.doc.doc_type.localeCompare(b.doc.doc_type)
        : b.doc.created_at.localeCompare(a.doc.created_at),
    );
  }, [rows, sort]);

  if (error) return <div className="card p-6 text-center text-sm text-red-600">{error}</div>;
  const pdfNotice = pdfError ? (
    <div className="mb-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
      {pdfError}
    </div>
  ) : null;
  if (!rows) {
    return <div className="p-8 text-center text-sm text-gray-500">Collecting documents…</div>;
  }

  return (
    <section className="card">
      {pdfNotice}
      <div className="flex items-center gap-2 border-b border-surface-border p-3">
        <div className="section-label">Documents</div>
        <select
          className="ml-auto rounded-md border border-surface-border px-2 py-1 text-xs"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          <option value="created_at">Newest first</option>
          <option value="doc_type">By type</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-500">
          No mediation documents yet — records of mediation and stipulations will appear here.
        </p>
      ) : (
        <ul className="divide-y divide-surface-border">
          {sorted.map(({ doc, caseItem }) => (
            <li key={doc.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{docTypeLabel(doc.doc_type)}</span>
                  <span className="text-xs text-gray-500">{doc.status}</span>
                </div>
                <button
                  type="button"
                  className="mt-0.5 text-xs text-gray-500 hover:text-brand hover:underline"
                  onClick={() => onOpenCase(caseItem.id)}
                >
                  {caseTitle(caseItem.metadata, caseItem.claims)}
                </button>
              </div>
              <span className="text-xs text-gray-400">{formatDate(doc.created_at)}</span>
              {doc.pdf_url && (
                <button
                  type="button"
                  className="btn-secondary !py-1 !text-xs"
                  onClick={() => {
                    void getDocumentPdfUrl(token, doc.id)
                      .then((u) => window.open(u, '_blank', 'noopener'))
                      .catch(() => setPdfError('No PDF is available for that document yet.'));
                  }}
                >
                  Open PDF
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
